import { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Building, Mail, MapPin, TrendingUp, Users, RefreshCw, Loader2, DollarSign, Calendar, UserPlus, Trash2, CheckCircle2, XCircle, Search, UserCircle } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function AdminCustomers() {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [localCustomers, setLocalCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [chartMogulStatus, setChartMogulStatus] = useState({ connected: false });
  const [refreshing, setRefreshing] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountManagers, setAccountManagers] = useState([]);
  const [assignedManagerId, setAssignedManagerId] = useState(null);
  const [assigningManager, setAssigningManager] = useState(false);

  // Filter and sort customers by MRR (highest first)
  const filteredCustomers = useMemo(() => {
    let filtered = customers;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = customers.filter(c => 
        (c.name || '').toLowerCase().includes(query) ||
        (c.company || '').toLowerCase().includes(query) ||
        (c.email || '').toLowerCase().includes(query)
      );
    }
    
    // Sort by MRR descending
    return [...filtered].sort((a, b) => (b.mrr || 0) - (a.mrr || 0));
  }, [customers, searchQuery]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch account managers
      const managersRes = await fetch(`${BACKEND_URL}/api/admin/account-managers`, { credentials: 'include' });
      if (managersRes.ok) {
        const managersData = await managersRes.json();
        setAccountManagers(managersData.managers || []);
      }

      // Check ChartMogul status
      const statusRes = await fetch(`${BACKEND_URL}/api/admin/chartmogul/status`, { credentials: 'include' });
      if (statusRes.ok) {
        const status = await statusRes.json();
        setChartMogulStatus(status);
        
        if (status.connected) {
          // Fetch ChartMogul customers
          const cmRes = await fetch(`${BACKEND_URL}/api/admin/chartmogul/customers`, { credentials: 'include' });
          if (cmRes.ok) {
            const cmData = await cmRes.json();
            setCustomers(cmData.customers || []);
          }
        }
      }
      
      // Also fetch local customers (like Xentra dummy account)
      const localRes = await fetch(`${BACKEND_URL}/api/admin/companies`, { credentials: 'include' });
      if (localRes.ok) {
        const localData = await localRes.json();
        // Match Xentra with flexible matching (handles trailing spaces)
        setLocalCustomers(localData.filter(c => (c.name || '').trim().toLowerCase() === 'xentra'));
      }
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Customer data refreshed');
  };

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCompanyUsers([]);
    setNewUserEmail('');
    setAssignedManagerId(null);
    
    const identifier = customer.chartmogul_uuid || customer.company_id;
    
    if (customer.source === 'chartmogul' && customer.chartmogul_uuid) {
      setLoadingDetail(true);
      try {
        const [detailRes, usersRes, managerRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/admin/chartmogul/customers/${customer.chartmogul_uuid}`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/admin/company-users/${customer.chartmogul_uuid}`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/admin/account-manager-assignment/${customer.chartmogul_uuid}`, { credentials: 'include' })
        ]);
        
        if (detailRes.ok) {
          const data = await detailRes.json();
          setCustomerDetail(data);
        }
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setCompanyUsers(usersData.assignments || []);
        }
        if (managerRes.ok) {
          const managerData = await managerRes.json();
          setAssignedManagerId(managerData.manager_id || null);
        }
      } catch (error) {
        toast.error('Failed to load customer details');
      } finally {
        setLoadingDetail(false);
      }
    } else if (customer.source === 'local' && customer.company_id) {
      setLoadingDetail(true);
      try {
        const [usersRes, managerRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/admin/company-users/${customer.company_id}`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/admin/account-manager-assignment/${customer.company_id}`, { credentials: 'include' })
        ]);
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setCompanyUsers(usersData.assignments || []);
        }
        if (managerRes.ok) {
          const managerData = await managerRes.json();
          setAssignedManagerId(managerData.manager_id || null);
        }
      } catch (error) {
        console.error('Failed to load company users');
      } finally {
        setLoadingDetail(false);
      }
      setCustomerDetail(null);
    } else {
      setCustomerDetail(null);
    }
  };

  const handleAssignManager = async (managerId) => {
    if (!selectedCustomer) return;
    
    setAssigningManager(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/account-manager-assignment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          manager_id: managerId || null,
          company_id: selectedCustomer.company_id || null,
          chartmogul_uuid: selectedCustomer.chartmogul_uuid || null
        })
      });
      
      if (response.ok) {
        setAssignedManagerId(managerId || null);
        toast.success(managerId ? 'Account manager assigned' : 'Account manager removed');
      } else {
        toast.error('Failed to update assignment');
      }
    } catch (error) {
      toast.error('Failed to update assignment');
    } finally {
      setAssigningManager(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUserEmail || !selectedCustomer) return;
    
    setAddingUser(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/company-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: newUserEmail,
          company_id: selectedCustomer.company_id || null,
          chartmogul_uuid: selectedCustomer.chartmogul_uuid || null,
          company_name: selectedCustomer.name || selectedCustomer.company || 'Unknown'
        })
      });
      
      if (response.ok) {
        toast.success(`User ${newUserEmail} added to ${selectedCustomer.name}`);
        setNewUserEmail('');
        // Refresh users list
        handleSelectCustomer(selectedCustomer);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add user');
      }
    } catch (error) {
      toast.error('Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleRemoveUser = async (email) => {
    if (!window.confirm(`Remove ${email} from this company?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/company-users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('User removed');
        handleSelectCustomer(selectedCustomer);
      } else {
        toast.error('Failed to remove user');
      }
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    const symbols = { GBP: '£', USD: '$', EUR: '€' };
    return `${symbols[currency] || currency}${(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  // Calculate totals
  const totalMRR = customers.reduce((sum, c) => sum + (c.mrr || 0), 0);
  const totalARR = customers.reduce((sum, c) => sum + (c.arr || 0), 0);
  const activeCustomers = customers.filter(c => c.status === 'Active').length;

  if (loading) {
    return (
      <AdminLayout title="Customers">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Customers">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building className="h-8 w-8 text-secondary" />
            <div>
              <h2 className="text-2xl font-mono font-bold">Customers</h2>
              <p className="text-sm text-muted-foreground">
                {chartMogulStatus.connected ? (
                  <span className="text-primary">● Connected to ChartMogul</span>
                ) : (
                  <span className="text-muted-foreground">ChartMogul not connected</span>
                )}
              </p>
            </div>
          </div>
          <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4 border-t-2 border-t-secondary">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-secondary" />
              <div>
                <p className="text-2xl font-mono font-bold">{customers.length}</p>
                <p className="text-sm text-muted-foreground font-mono">Total Customers</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 border-t-2 border-t-primary">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-mono font-bold">{activeCustomers}</p>
                <p className="text-sm text-muted-foreground font-mono">Active</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 border-t-2 border-t-accent">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-accent" />
              <div>
                <p className="text-2xl font-mono font-bold">{formatCurrency(totalMRR)}</p>
                <p className="text-sm text-muted-foreground font-mono">Total MRR</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 border-t-2 border-t-secondary">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-secondary" />
              <div>
                <p className="text-2xl font-mono font-bold">{formatCurrency(totalARR)}</p>
                <p className="text-sm text-muted-foreground font-mono">Total ARR</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer List */}
          <div className="lg:col-span-2 bg-card border border-border rounded-lg">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-mono font-bold">Customer List</h3>
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search customers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
              {/* Local customers (Xentra) */}
              {localCustomers.map((customer) => (
                <button
                  key={customer.company_id}
                  onClick={() => handleSelectCustomer({ ...customer, source: 'local' })}
                  className={`w-full text-left p-4 hover:bg-muted/50 transition-colors ${
                    selectedCustomer?.company_id === customer.company_id ? 'bg-muted/50 border-l-2 border-l-secondary' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-secondary/20 flex items-center justify-center">
                        <Building className="h-5 w-5 text-secondary" />
                      </div>
                      <div>
                        <p className="font-mono font-bold">{customer.name}</p>
                        <p className="text-xs text-muted-foreground">Local Account (Test)</p>
                      </div>
                    </div>
                    <span className="px-2 py-1 rounded-full text-xs font-mono bg-secondary/20 text-secondary">
                      INTERNAL
                    </span>
                  </div>
                </button>
              ))}
              
              {/* ChartMogul customers - sorted by MRR */}
              {filteredCustomers.map((customer) => (
                <button
                  key={customer.chartmogul_uuid}
                  onClick={() => handleSelectCustomer(customer)}
                  className={`w-full text-left p-4 hover:bg-muted/50 transition-colors ${
                    selectedCustomer?.chartmogul_uuid === customer.chartmogul_uuid ? 'bg-muted/50 border-l-2 border-l-primary' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Building className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-mono font-bold">{customer.name || customer.company}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {customer.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {customer.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-bold text-primary">{formatCurrency(customer.mrr, customer.currency)}</p>
                      <p className="text-xs text-muted-foreground">MRR</p>
                    </div>
                  </div>
                </button>
              ))}
              
              {filteredCustomers.length === 0 && localCustomers.length === 0 && (
                <div className="p-8 text-center">
                  <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No customers found</p>
                </div>
              )}
            </div>
          </div>

          {/* Customer Detail Panel */}
          <div className="bg-card border border-border rounded-lg">
            <div className="p-4 border-b border-border">
              <h3 className="font-mono font-bold">Customer Details</h3>
            </div>
            {loadingDetail ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : selectedCustomer ? (
              <div className="p-4 space-y-4">
                {/* Customer Info */}
                <div>
                  <h4 className="text-xl font-mono font-bold">{selectedCustomer.name || selectedCustomer.company}</h4>
                  {selectedCustomer.email && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {selectedCustomer.email}
                    </p>
                  )}
                  {(selectedCustomer.city || selectedCustomer.country) && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {[selectedCustomer.city, selectedCustomer.country].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>

                {/* Revenue Info */}
                {selectedCustomer.source === 'chartmogul' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-muted/20 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground font-mono">MRR</p>
                      <p className="text-lg font-mono font-bold text-primary">
                        {formatCurrency(selectedCustomer.mrr, selectedCustomer.currency)}
                      </p>
                    </div>
                    <div className="bg-muted/20 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground font-mono">ARR</p>
                      <p className="text-lg font-mono font-bold text-secondary">
                        {formatCurrency(selectedCustomer.arr, selectedCustomer.currency)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Customer Since */}
                {selectedCustomer.customer_since && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Customer since:</span>
                    <span className="font-mono">{formatDate(selectedCustomer.customer_since)}</span>
                  </div>
                )}

                {/* Subscriptions */}
                {customerDetail?.subscriptions && customerDetail.subscriptions.length > 0 && (
                  <div>
                    <h5 className="font-mono font-bold text-sm mb-2">Subscriptions</h5>
                    <div className="space-y-2">
                      {customerDetail.subscriptions.map((sub, idx) => (
                        <div key={sub.uuid || idx} className="bg-muted/20 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-mono font-bold text-sm">{sub.plan || 'Subscription'}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-mono ${
                              sub.status === 'Active' ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                            }`}>
                              {sub.status}
                            </span>
                          </div>
                          <p className="text-sm text-primary font-mono">{formatCurrency(sub.mrr, sub.currency)}/mo</p>
                          {sub.start_date && (
                            <p className="text-xs text-muted-foreground">Started: {formatDate(sub.start_date)}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status */}
                <div className="pt-2 border-t border-border">
                  <span className={`px-3 py-1 rounded-full text-xs font-mono ${
                    selectedCustomer.status === 'Active' 
                      ? 'bg-primary/20 text-primary' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {selectedCustomer.status || 'Active'}
                  </span>
                </div>

                {/* Users Section */}
                <div className="pt-4 border-t border-border">
                  <h5 className="font-mono font-bold text-sm mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Assigned Users
                  </h5>
                  
                  {/* Add User Form */}
                  <div className="flex gap-2 mb-3">
                    <Input
                      type="email"
                      placeholder="user@email.com"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      className="flex-1 text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddUser()}
                    />
                    <Button 
                      size="sm" 
                      onClick={handleAddUser}
                      disabled={addingUser || !newUserEmail}
                    >
                      {addingUser ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                    </Button>
                  </div>

                  {/* Users List */}
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {companyUsers.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        No users assigned. Add an email above.
                      </p>
                    ) : (
                      companyUsers.map((u) => (
                        <div key={u.email} className="flex items-center justify-between bg-muted/20 rounded-md p-2">
                          <div className="flex items-center gap-2">
                            {u.user_exists ? (
                              <CheckCircle2 className="h-4 w-4 text-primary" />
                            ) : (
                              <XCircle className="h-4 w-4 text-muted-foreground" />
                            )}
                            <div>
                              <p className="text-sm font-mono">{u.email}</p>
                              {u.user_name && (
                                <p className="text-xs text-muted-foreground">{u.user_name}</p>
                              )}
                              {!u.user_exists && (
                                <p className="text-xs text-accent">Pending login</p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveUser(u.email)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground mt-2">
                    Users will be auto-linked when they login with the assigned email.
                  </p>
                </div>

                {/* Account Manager Section */}
                <div className="pt-4 border-t border-border">
                  <h5 className="font-mono font-bold text-sm mb-3 flex items-center gap-2">
                    <UserCircle className="h-4 w-4" />
                    Account Manager
                  </h5>
                  
                  {accountManagers.length === 0 ? (
                    <div className="text-center py-3 bg-muted/20 rounded-md">
                      <p className="text-xs text-muted-foreground mb-2">No account managers created yet</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => window.location.href = '/admin/account-managers'}
                      >
                        Create Account Manager
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <select
                        value={assignedManagerId || ''}
                        onChange={(e) => handleAssignManager(e.target.value || null)}
                        disabled={assigningManager}
                        className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">-- No Account Manager --</option>
                        {accountManagers.map((m) => (
                          <option key={m.manager_id} value={m.manager_id}>
                            {m.name} {m.job_title ? `(${m.job_title})` : ''}
                          </option>
                        ))}
                      </select>
                      
                      {assignedManagerId && (
                        <div className="bg-primary/10 border border-primary/20 rounded-md p-3">
                          {(() => {
                            const manager = accountManagers.find(m => m.manager_id === assignedManagerId);
                            if (!manager) return null;
                            return (
                              <div className="flex items-center gap-3">
                                {manager.profile_picture ? (
                                  <img src={manager.profile_picture} alt={manager.name} className="h-10 w-10 rounded-full object-cover" />
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                                    <span className="text-sm font-mono font-bold text-primary">
                                      {manager.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                    </span>
                                  </div>
                                )}
                                <div>
                                  <p className="font-mono font-bold text-sm">{manager.name}</p>
                                  {manager.job_title && <p className="text-xs text-muted-foreground">{manager.job_title}</p>}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                <Building className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a customer to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminCustomers;
